import { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';

import { NotificationType } from '../../config';
import supportingDocumentTypeService from '../../service/supportingDocumentType.service';
import addNotification from '../../utils/addNotification';
import { ButtonUI, CheckboxUI, DialogUI } from '../Interface';

const SupportingDocumentTypeItemLinkDialog = ({
  open,
  onClose,
  itemCode,
  itemCodes,
  editable = false,
  batchManaged = false,
}) => {
  const [supportingDocumentTypes, setSupportingDocumentTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [itemLinks, setItemLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        if (editable && itemCode) {
          const [types, links] = await Promise.all([
            supportingDocumentTypeService.getItems(),
            supportingDocumentTypeService.getItemLinks(itemCode),
          ]);

          setSupportingDocumentTypes(Array.isArray(types) ? types : types?.rows ?? []);
          setSelectedIds(
            (Array.isArray(links) ? links : []).map((link) => Number(link.supportingDocumentTypeId))
          );
          setItemLinks([]);
          return;
        }

        const uniqueItemCodes = [...new Set((itemCodes ?? [itemCode]).filter(Boolean))];
        const links = await Promise.all(
          uniqueItemCodes.map(async (currentItemCode) => ({
            itemCode: currentItemCode,
            links: await supportingDocumentTypeService.getItemLinks(currentItemCode),
          }))
        );
        setItemLinks(links);
        setSupportingDocumentTypes([]);
        setSelectedIds([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [editable, itemCode, itemCodes, open]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleChange = (id, checked) => {
    setSelectedIds((currentIds) =>
      checked ? [...currentIds, Number(id)] : currentIds.filter((currentId) => currentId !== Number(id))
    );
  };

  const handleSave = async () => {
    if (!itemCode || isSaving) return;

    setIsSaving(true);
    try {
      await supportingDocumentTypeService.updateItemLinks(itemCode, selectedIds);
      addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent="Kísérő dokumentumok"
      fullWidth
      maxWidth="sm"
    >
      <Box minHeight="8rem" mt="1rem">
        {isLoading ? (
          <Box display="flex" justifyContent="center" py="2rem">
            <CircularProgress size={32} />
          </Box>
        ) : editable ? (
          supportingDocumentTypes.length > 0 ? (
            supportingDocumentTypes.map((type) => {
              const selected = selectedIds.includes(Number(type.id));
              const externalDisabled = !type.generated && !batchManaged && !selected;

              return (
                <Tooltip
                  key={type.id}
                  title={externalDisabled ? 'Külső típus csak sarzskezelt cikkhez választható.' : ''}
                >
                  <Box display="flex" alignItems="center">
                    <CheckboxUI
                      sx={{ margin: 0, padding: '0.35rem' }}
                      checked={selected}
                      disabled={isSaving || externalDisabled}
                      onChange={(event) => handleChange(type.id, event.target.checked)}
                    />
                    <Typography color={externalDisabled ? 'text.secondary' : 'text.primary'}>
                      {type.name} – {type.generated ? 'Generált' : 'Külső'}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })
          ) : (
            <Typography>Nincs választható kísérő dokumentum típus.</Typography>
          )
        ) : itemLinks.length > 0 ? (
          itemLinks.map((item) => {
            const links = Array.isArray(item.links) ? item.links : [];

            return (
              <Box key={item.itemCode} mb="1rem">
                <Typography fontWeight="bold">{item.itemCode}</Typography>
                {links.length > 0 ? (
                  links.map((link) => (
                    <Typography key={link.supportingDocumentTypeId} pl="1rem">
                      {link.supportingDocumentType?.name}
                    </Typography>
                  ))
                ) : (
                  <Typography pl="1rem">Nincs kijelölt kísérő dokumentum típus.</Typography>
                )}
              </Box>
            );
          })
        ) : (
          <Typography>Nincs megjeleníthető cikk.</Typography>
        )}
      </Box>

      <Box display="flex" gap="0.5rem" justifyContent="center" mt="1rem">
        {editable && (
          <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} disabled={isLoading || isSaving} />
        )}
        <ButtonUI text={editable ? 'Mégse' : 'Bezárás'} className="bg-primary" onClick={handleClose} disabled={isSaving} />
      </Box>
    </DialogUI>
  );
};

export default SupportingDocumentTypeItemLinkDialog;
